import {
  Box,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  makeStyles
} from '@material-ui/core'
import React from 'react'

import {
  fetchAptBudgets,
  fetchAptList,
  saveAptBudget
} from 'api/apartmentProcurement'

import { useAptCategories } from './aptCategories'
import { money } from './constants'
import { RoleContext } from './roleContext'

// One pot per (apartment x category) — six heads, no monthly reset. The At
// Apartment head (and HQ) edit inline; a cell saves on blur. Everyone else
// sees the grid read-only.
//
// The six heads split into two age bands (P7, 2026-07-18): a view dropdown
// shows just the 3-5 columns, just the 5+ columns, or an Overall roll-up of
// the two band totals — the full six-column grid was cramped. A category added
// from Alfred (e.g. "5+ Music", 2026-09-17) joins its band as another column.
const bandsOf = (categories) => ({
  '3-5': categories.filter((c) => c.startsWith('3-5')),
  '5+': categories.filter((c) => c.startsWith('5+'))
})
const VIEW_OPTIONS = [
  { value: '3-5', label: '3-5 (age band)' },
  { value: '5+', label: '5+ (age band)' },
  { value: 'overall', label: 'Overall' }
]

const useStyles = makeStyles((theme) => ({
  paper: { padding: theme.spacing(2) },
  head: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1),
    flexWrap: 'wrap'
  },
  cell: { minWidth: 96 },
  cluster: { color: '#888' },
  total: { fontWeight: 700 },
  saved: { color: '#4caf50', marginLeft: theme.spacing(1) }
}))

const BudgetsTab = () => {
  const classes = useStyles()
  const role = React.useContext(RoleContext)
  const categories = useAptCategories()
  const [apartments, setApartments] = React.useState(null)
  const [budgets, setBudgets] = React.useState(null)
  const [view, setView] = React.useState('3-5')
  const [savedFlash, setSavedFlash] = React.useState('')

  const canEdit = role.is_central || role.is_hq

  React.useEffect(() => {
    fetchAptList().then(setApartments)
    fetchAptBudgets().then(setBudgets)
  }, [])

  if (!apartments || !budgets) return <LinearProgress />

  const BANDS = bandsOf(categories)

  const amountOf = (apartmentId, category) => {
    const row = budgets.find(
      (b) => b.apartment === apartmentId && b.category === category
    )
    return row ? Number(row.amount) : ''
  }

  const bandTotal = (apartmentId, band) =>
    BANDS[band].reduce((s, c) => s + (Number(amountOf(apartmentId, c)) || 0), 0)

  const save = async (apartment, category, raw) => {
    const amount = Number(raw)
    if (raw === '' || Number.isNaN(amount) || amount < 0) return
    if (amount === amountOf(apartment, category)) return
    const saved = await saveAptBudget({ apartment, category, amount })
    setBudgets((prev) => {
      const rest = prev.filter(
        (b) => !(b.apartment === apartment && b.category === category)
      )
      return [...rest, saved]
    })
    setSavedFlash(`${category} saved`)
    setTimeout(() => setSavedFlash(''), 1500)
  }

  const isOverall = view === 'overall'
  const cols = isOverall ? [] : BANDS[view]

  return (
    <Paper className={classes.paper} variant="outlined">
      <Box className={classes.head}>
        <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
          Budgets (one pot per apartment × category)
        </Typography>
        <Select
          value={view}
          onChange={(e) => setView(e.target.value)}
          style={{ fontSize: 14 }}>
          {VIEW_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </Select>
        {savedFlash && (
          <Typography variant="caption" className={classes.saved}>
            ✓ {savedFlash}
          </Typography>
        )}
      </Box>
      {!canEdit && (
        <Typography variant="caption" color="textSecondary">
          Read-only — budgets are set by the At Apartment head.
        </Typography>
      )}
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Apartment</TableCell>
            {isOverall ? (
              <>
                <TableCell align="right">3-5 total</TableCell>
                <TableCell align="right">5+ total</TableCell>
              </>
            ) : (
              cols.map((c) => (
                <TableCell key={c} align="right">
                  {c}
                </TableCell>
              ))
            )}
            <TableCell align="right">Total</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {apartments.map((apt) => {
            const total = categories.reduce(
              (sum, c) => sum + (Number(amountOf(apt.uuid, c)) || 0),
              0
            )
            return (
              <TableRow key={apt.uuid}>
                <TableCell>
                  {apt.name}{' '}
                  {apt.cluster && (
                    <span className={classes.cluster}>({apt.cluster})</span>
                  )}
                </TableCell>
                {isOverall ? (
                  <>
                    <TableCell align="right">
                      {money(bandTotal(apt.uuid, '3-5'))}
                    </TableCell>
                    <TableCell align="right">
                      {money(bandTotal(apt.uuid, '5+'))}
                    </TableCell>
                  </>
                ) : (
                  cols.map((category) => (
                    <TableCell
                      key={category}
                      align="right"
                      className={classes.cell}>
                      {canEdit ? (
                        <TextField
                          type="number"
                          defaultValue={amountOf(apt.uuid, category)}
                          inputProps={{ style: { textAlign: 'right' } }}
                          onBlur={(e) => save(apt.uuid, category, e.target.value)}
                        />
                      ) : (
                        money(amountOf(apt.uuid, category) || 0)
                      )}
                    </TableCell>
                  ))
                )}
                <TableCell align="right" className={classes.total}>
                  {money(total)}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </Paper>
  )
}

export default BudgetsTab
